import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailHistoryAPI, EmailHistory, EmailStatus } from '../../services/api';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  QueueListIcon,
  DocumentTextIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { Card, CardBody, Badge, Spinner, EmptyState, SearchInput, Button } from '../ui';

const EmailHistoryList: React.FC = () => {
  const navigate = useNavigate();
  const [emails, setEmails] = useState<EmailHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<EmailStatus | 'ALL'>('ALL');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchEmails();
  }, [pagination.page, statusFilter]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const response = await emailHistoryAPI.getAll({
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
      });
      setEmails(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (error) {
      console.error('Error fetching emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusVariant = (status: EmailStatus): 'success' | 'error' | 'primary' | 'warning' | 'neutral' => {
    switch (status) {
      case 'SENT': return 'success';
      case 'FAILED': return 'error';
      case 'SCHEDULED': return 'primary';
      case 'QUEUED': return 'warning';
      case 'DRAFT': return 'neutral';
      default: return 'neutral';
    }
  };

  const getStatusIcon = (status: EmailStatus) => {
    switch (status) {
      case 'SENT': return <CheckCircleIcon className="h-4 w-4 mr-1" />;
      case 'FAILED': return <XCircleIcon className="h-4 w-4 mr-1" />;
      case 'SCHEDULED': return <ClockIcon className="h-4 w-4 mr-1" />;
      case 'QUEUED': return <QueueListIcon className="h-4 w-4 mr-1" />;
      case 'DRAFT': return <DocumentTextIcon className="h-4 w-4 mr-1" />;
      default: return null;
    }
  };

  const filteredEmails = emails.filter(email =>
    email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    email.to.some(to => to.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <Card variant="gradient" className="mb-8">
        <CardBody className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Email History</h1>
              <p className="text-primary-100">View all sent and scheduled emails</p>
            </div>
            <EnvelopeIcon className="h-16 w-16 text-white opacity-20" />
          </div>
        </CardBody>
      </Card>

      {/* Filters */}
      <Card variant="elevated" className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search by subject or recipient..."
            />

            {/* Status Filter */}
            <div className="relative">
              <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-neutral-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as EmailStatus | 'ALL')}
                className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none transition-colors"
              >
                <option value="ALL">All Status</option>
                <option value="SENT">Sent</option>
                <option value="FAILED">Failed</option>
                <option value="SCHEDULED">Scheduled</option>
                <option value="QUEUED">Queued</option>
                <option value="DRAFT">Draft</option>
              </select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Email List */}
      <Card variant="elevated">
        <CardBody className="p-0">
          {filteredEmails.length === 0 ? (
            <EmptyState
              icon={<EnvelopeIcon className="h-12 w-12" />}
              title="No emails found"
              description={searchTerm || statusFilter !== 'ALL'
                ? 'Try adjusting your filters'
                : 'No emails have been sent yet'}
            />
          ) : (
            <div className="divide-y divide-neutral-200">
              {filteredEmails.map((email) => (
                <div
                  key={email.id}
                  onClick={() => navigate(`/clients/emails/${email.id}`)}
                  className="p-6 hover:bg-neutral-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Subject */}
                      <h3 className="text-lg font-semibold text-neutral-900 mb-2 truncate">
                        {email.subject}
                      </h3>

                      {/* Recipients */}
                      <div className="flex items-center text-sm text-neutral-600 mb-2">
                        <span className="font-medium mr-2">To:</span>
                        <span className="truncate">{email.to.join(', ')}</span>
                      </div>

                      {/* Metadata */}
                      <div className="flex items-center gap-4 text-sm text-neutral-500">
                        {email.client && (
                          <span className="flex items-center">
                            <DocumentTextIcon className="h-4 w-4 mr-1" />
                            {email.client.name}
                          </span>
                        )}
                        {email.sentAt && (
                          <span className="flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {new Date(email.sentAt).toLocaleString()}
                          </span>
                        )}
                        {email.scheduledFor && !email.sentAt && (
                          <span className="flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            Scheduled: {new Date(email.scheduledFor).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="ml-4 flex-shrink-0">
                      <Badge variant={getStatusVariant(email.status)}>
                        <span className="flex items-center">
                          {getStatusIcon(email.status)}
                          {email.status}
                        </span>
                      </Badge>
                    </div>
                  </div>

                  {/* Error Message */}
                  {email.error && (
                    <div className="mt-3 p-3 bg-error-50 border border-error-200 rounded-lg">
                      <p className="text-sm text-error-800">
                        <span className="font-medium">Error:</span> {email.error}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-neutral-700">
            Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
            <span className="font-medium">
              {Math.min(pagination.page * pagination.limit, pagination.total)}
            </span>{' '}
            of <span className="font-medium">{pagination.total}</span> emails
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailHistoryList;

